import axios from "axios";
import React, { useState, useCallback, useMemo, forwardRef } from "react";

import { StaticQuery, graphql } from "gatsby";
import { Circle } from "rc-progress";
import Uploady, {
  useUploadyContext,
  useBatchAddListener,
  useRequestPreSend,
  useBatchStartListener,
  useItemProgressListener,
  useBatchFinishListener,
} from "@rpldy/uploady";
import UploadDropZone from "@rpldy/upload-drop-zone";
import { asUploadButton } from "@rpldy/upload-button";
import {
  Row,
  Col,
  Button,
  Alert,
  Form,
  FormGroup,
  FormText,
  Label,
  Input,
} from "reactstrap";
import * as Styles from "./uploader.module.scss";

const UploadProgress = (props) => {
  const progressData = useItemProgressListener() || { completed: 0 };
  const { completed } = progressData;

  return (
    <Alert hidden={completed === 0}>
      <h4 className="alert-heading">
        <Circle
          className={Styles.progressCircle}
          strokeWidth={5}
          strokeColor={completed === 100 ? "#4dc14d" : "#40affd"}
          percent={completed}
        />
        <span>{completed === 100 ? " Upload Complete!" : "Uploading..."}</span>
      </h4>
      <p className="mb-0" hidden={completed < 100}>
        Thank you for submitting your resume. Someone will be in touch soon. If
        you'd like, you may also fill out our online application form{" "}
        <a
          href={
            "https://internal.unitedlabsinc.com/recruiting/app/?recemail=" +
            props.recemail
          }
          target="_blank"
          rel="noreferrer noopener"
        >
          here.
        </a>
      </p>
    </Alert>
  );
};
const UploadButton = asUploadButton(
  forwardRef((props, ref) => <Button {...props}>{props.text}</Button>)
);

const SignedUploadDragAndDrop = (props) => {
  const [fields, setFields] = useState({});
  const [recemail, setRecemail] = useState(props.recemail);
  const [fileName, setFileName] = useState(null);
  const [preButtonText, setPreButtonText] = useState(
    "Drag and drop your file, or"
  );
  const [buttonText, setButtonText] = useState("Select file");
  const [postButtonText, setPostButtonText] = useState(
    "(Must be a .pdf file, max size: 5MB)"
  );
  const [fileUploadComplete, setFileUploadComplete] = useState(false);
  const [fileAdded, setFileAdded] = useState(false);
  const uploadyContext = useUploadyContext();
  React.useEffect(() => {
    setRecemail(props.recemail);
  }, [props.recemail]);
  useBatchAddListener((batch) => {
    setPreButtonText("You have selected this file:");
    setButtonText(batch.items[0].file.name);
    setPostButtonText('Click "Send" Below to send this resume file');
    setFileName(batch.items[0].file.name);
    setFileAdded(true);
  });
  useBatchFinishListener(() => {
    setPreButtonText(" ");
    setButtonText("Upload Complete");
    setPostButtonText(" ");
    setFileUploadComplete(true);
  });
  const buttonExtraProps = {
    text: buttonText,
    color: "primary",
    disabled: fileUploadComplete,
  };
  const handleRecSelectChange = (event) => {
    setRecemail(event.target.value);
  };
  const onSubmit = useCallback(() => {
    uploadyContext.processPending({ params: fields });
  }, [fields, uploadyContext]);

  useRequestPreSend(async ({ items, options }) => {
    const files = items.length > 0 ? items[0] : {};
    let { file } = files;
    let { name, type } = file;
    const emailhandleregex = /([^<@]+)/;
    name = emailhandleregex.exec(recemail)[0] + "_" + name;
    let gateway =
      "https://2j44v0sobd.execute-api.us-east-1.amazonaws.com/default/getResumeUploadURL";
    const response = await axios(
      `${gateway}?` +
        new URLSearchParams({
          name,
          type,
        })
    );
    let { data } = response;
    let uploadUrl = data.upload_url;

    return {
      options: {
        sendWithFormData: false,
        destination: {
          url: uploadUrl,
          method: "PUT",
          headers: {
            "Content-Type": type,
          },
        },
      },
    };
  });

  return (
    <Form>
      <StaticQuery
        query={graphql`
          {
            allRecruiter {
              nodes {
                email
                firstName
                lastName
              }
            }
          }
        `}
        render={(data) => (
          <FormGroup row>
            <Col md="5">
              <Label for="exampleSelect" style={{ float: "right" }}>
                Your Recruiter:
              </Label>
            </Col>

            <Col md="6">
              <Input
                id="exampleSelect"
                name="select"
                type="select"
                className={Styles.codeInput}
                onChange={handleRecSelectChange}
                value={recemail}
              >
                <option value="recruiter@unitedlabsinc.com"> </option>
                {data.allRecruiter.nodes
                  .sort((a, b) => (a.lastName > b.lastName ? 1 : -1))
                  .map((recruiter, index) => (
                    <option value={recruiter.email} key={recruiter.email}>
                      {recruiter.firstName} {recruiter.lastName}
                    </option>
                  ))}
              </Input>
            </Col>
          </FormGroup>
        )}
      ></StaticQuery>
      <UploadDropZone
        className={Styles.dragDrop}
        onDragOverClassName={Styles.dragOver}
        autoUpload={false}
      >
        <FormGroup>
          <br />
          <br />
          {preButtonText}
          <br />
          <UploadButton autoUpload={false} extraProps={buttonExtraProps} />
          <br />

          <FormText>{postButtonText}</FormText>
        </FormGroup>
      </UploadDropZone>
      <Row>
        <Col className={Styles.sendBtnCol}>
          <Button
            type="button"
            color="success"
            onClick={onSubmit}
            disabled={fileUploadComplete || !fileAdded}
          >
            Send
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default function Upload(props) {
  const fileFilter = useCallback((file) => {
    //filter out files larger than 5MB
    return file.size < 5242880 && file.type === "application/pdf";
  }, []);
  return (
    <div>
      <Uploady
        destination={{}}
        multiple={false}
        accept=".pdf"
        autoUpload={true}
        fileFilter={fileFilter}
      >
        <SignedUploadDragAndDrop recemail={props.recemail} />
        <UploadProgress recemail={props.recemail} />
      </Uploady>
    </div>
  );
}
